import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { color } from '../../../app/theme';
import Template from './Template';
import TemplateDropdown from './TemplateDropdown';
import Icons from './Icons';
import IconEdit from '../../../assets/images/Icons/IconEdit';
import ImageCardDialog from '../ImageCardDialog';
import { setState } from '../../../features/mainSlice';

const useStyles = makeStyles(
  () => ({
    root: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 12,
    },
    dropdown: {
      flex: '0 0 auto',
    },
    contentContainer: {
      position: 'relative',
      width: '100%',
    },
    content: {
      cursor: 'pointer',
      width: '100%',
      height: 44,
      fontSize: 15,
      lineHeight: 1,
      borderRadius: 24,
      border: 'solid 1px #bdbdbd',
      backgroundColor: '#ffffff',
      padding: '15px 48px 14px 24px',
      resize: 'none',
      outline: 'none',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      '&::placeholder': {
        fontSize: 15,
        color: color.$3,
      },
    },
    error: { border: `solid 1px ${color.$17}` },
    icon: {
      cursor: 'pointer',
      position: 'absolute',
      top: 10,
      right: 16,
    },
  }),
  {
    name: 'ImageCardTemplate',
  },
);

function ImageCardTemplate({
  template,
  onChange,
  onChangeType,
  onDelete,
  platformType,
  onUp,
  onDown,
  messageLength,
  messageIndex,
  disableTemplate,
  isError,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isShowImageCardDialog = useSelector(
    store => store.ma.isShowImageCardDialog,
  );
  const isShowCardDialogTemplateIndex = useSelector(
    store => store.ma.isShowCardDialogTemplateIndex,
  );

  function handleOpenOptionalCardDialog() {
    dispatch(
      setState({
        isShowImageCardDialog: true,
        isShowCardDialogTemplateIndex: messageIndex,
      }),
    );
  }

  function handleOnSubmit(cardData, name) {
    dispatch(
      setState({
        isShowImageCardDialog: false,
      }),
    );
    onChange({
      name,
      contentType: 'application/x-template',
      data: {
        templateType: 'image',
        elements: cardData,
      },
    });
  }

  function handleOnCancel() {
    dispatch(
      setState({
        isShowImageCardDialog: false,
      }),
    );
  }

  return (
    <Template>
      <div className={classes.root}>
        <TemplateDropdown
          className={classes.dropdown}
          platformType={platformType}
          messageTemplatetype={
            template.templateType === 'image' ||
            template.data.templateType === 'image'
              ? 'image'
              : ''
          }
          onChange={onChangeType}
        />
        <Icons
          index={template.index}
          onUp={onUp}
          onDown={onDown}
          onDelete={onDelete}
          messageLength={messageLength}
        />
      </div>
      <div
        className={classes.contentContainer}
        onClick={handleOpenOptionalCardDialog}
      >
        <input
          className={classnames(classes.content, { [classes.error]: isError })}
          value={template.name ?? ''}
          placeholder={t('templates.edit-image-card')}
          readOnly
        />
        <div className={classes.icon}>
          <IconEdit
            className={classes.iconSvg}
            id={`edit-${uuidv4()}`}
            color={color.$10}
            hoverColor={color.$10}
          />
        </div>
      </div>
      {isShowImageCardDialog &&
        isShowCardDialogTemplateIndex === messageIndex && (
          <ImageCardDialog
            name={template.name}
            templateExpiredAt={template?.templateExpiredAt}
            templateId={template.templateId}
            data={template.data.elements}
            onSubmit={handleOnSubmit}
            onCancel={handleOnCancel}
            messageIndex={template.index}
            disableTemplate={disableTemplate}
          />
        )}
    </Template>
  );
}

ImageCardTemplate.defaultProps = {
  onChange: () => {},
  onChangeType: () => {},
  onDelete: () => {},
  onUp: () => {},
  onDown: () => {},
  messageLength: 0,
};

ImageCardTemplate.propTypes = {
  template: PropTypes.shape({
    templateType: PropTypes.oneOf(['image']),
    data: PropTypes.shape({
      content: PropTypes.string,
    }),
  }).isRequired,
  onChange: PropTypes.func,
  onChangeType: PropTypes.func,
  onDelete: PropTypes.func,
  onUp: PropTypes.func,
  onDown: PropTypes.func,
  messageLength: PropTypes.number,
};

export default ImageCardTemplate;
